import type { LucideIcon } from 'lucide-react';
import type { HelpTopicDetail } from './help-topic-detail.types';
import {
  Building2,
  ClipboardCheck,
  Code2,
  GraduationCap,
  Headphones,
  LineChart,
  Mic2,
  Palette,
  Puzzle,
  Rocket,
  Share2,
  Shield,
  Users,
  Wrench,
} from 'lucide-react';

/** Contenu éditorial du centre d’aide — à brancher plus tard sur CMS / i18n / API. */
export interface HelpTopic {
  id: string;
  title: string;
  description: string;
  /** Corps de modale déjà choisi pour la langue courante. Absent : fiche statique. */
  detail?: HelpTopicDetail | null;
}

export interface HelpCategory {
  id: string;
  title: string;
  description: string;
  icon: LucideIcon;
  topics: HelpTopic[];
}

/** Fiche aide qui ouvre le player onboarding (modale iframe) au lieu de la modale « fiche ». */
export const HELP_TOPIC_ONBOARDING_DASHBOARD_ID = 'demarrage-tableau-bord';
export const HELP_TOPIC_ONBOARDING_NAVIGATION_ID = 'demarrage-navigation';
export const HELP_TOPIC_ONBOARDING_PROFILE_ID = 'demarrage-profil';
export const HELP_TOPIC_ONBOARDING_ROLES_ID = 'demarrage-roles';
export const HELP_TOPIC_ONBOARDING_BLOCK_TYPES_ID = 'eduflow-blocs';
export const HELP_TOPIC_ONBOARDING_DESIGNER_OVERVIEW_ID = 'eduflow-concept';
export const HELP_TOPIC_ONBOARDING_BRANCHES_ID = 'eduflow-branches';
export const HELP_TOPIC_ONBOARDING_VALIDATION_ID = 'eduflow-validation';
export const HELP_TOPIC_ONBOARDING_OBJECTIVES_ID = 'eduflow-objectifs';
export const HELP_TOPIC_ONBOARDING_AI_DESIGNER_ID = 'eduflow-ia';
export const HELP_TOPIC_ONBOARDING_PUBLICATION_STATUS_ID = 'pub-statuts';
export const HELP_TOPIC_ONBOARDING_PUBLICATION_LINKS_ID = 'pub-liens';
export const HELP_TOPIC_ONBOARDING_CATALOG_VISIBILITY_ID = 'pub-catalogue';
export const HELP_TOPIC_ONBOARDING_ENROLLMENTS_ID = 'app-inscriptions';
export const HELP_TOPIC_ONBOARDING_GROUPS_COHORTS_ID = 'app-groupes';
export const HELP_TOPIC_ONBOARDING_PROGRESS_ID = 'app-progression';
export const HELP_TOPIC_ONBOARDING_CERTIFICATES_ID = 'app-certificats';
export const HELP_TOPIC_ONBOARDING_MEDIA_LIBRARY_ID = 'medias-bibliotheque';

/** Sujets qui ouvrent le player onboarding en iframe (pas la modale « fiche »). */
export const ONBOARDING_HELP_TOPIC_IDS: ReadonlySet<string> = new Set([
  HELP_TOPIC_ONBOARDING_DASHBOARD_ID,
  HELP_TOPIC_ONBOARDING_NAVIGATION_ID,
  HELP_TOPIC_ONBOARDING_PROFILE_ID,
  HELP_TOPIC_ONBOARDING_ROLES_ID,
  HELP_TOPIC_ONBOARDING_BLOCK_TYPES_ID,
  HELP_TOPIC_ONBOARDING_DESIGNER_OVERVIEW_ID,
  HELP_TOPIC_ONBOARDING_BRANCHES_ID,
  HELP_TOPIC_ONBOARDING_VALIDATION_ID,
  HELP_TOPIC_ONBOARDING_OBJECTIVES_ID,
  HELP_TOPIC_ONBOARDING_AI_DESIGNER_ID,
  HELP_TOPIC_ONBOARDING_PUBLICATION_STATUS_ID,
  HELP_TOPIC_ONBOARDING_PUBLICATION_LINKS_ID,
  HELP_TOPIC_ONBOARDING_CATALOG_VISIBILITY_ID,
  HELP_TOPIC_ONBOARDING_ENROLLMENTS_ID,
  HELP_TOPIC_ONBOARDING_GROUPS_COHORTS_ID,
  HELP_TOPIC_ONBOARDING_PROGRESS_ID,
  HELP_TOPIC_ONBOARDING_CERTIFICATES_ID,
  HELP_TOPIC_ONBOARDING_MEDIA_LIBRARY_ID,
]);

export function isOnboardingHelpTopicId(topicId: string): boolean {
  return ONBOARDING_HELP_TOPIC_IDS.has(topicId);
}

/** Résout une fiche : d’abord l’arborescence API (si fournie), puis le catalogue statique de secours. */
export function findHelpTopicById(
  topicId: string,
  primaryCategories?: readonly HelpCategory[] | null,
): { category: HelpCategory; topic: HelpTopic } | null {
  const pools: HelpCategory[][] = [];
  if (primaryCategories?.length) pools.push([...primaryCategories]);
  pools.push(HELP_CENTER_CATEGORIES);
  for (const categories of pools) {
    for (const category of categories) {
      const topic = category.topics.find((t) => t.id === topicId);
      if (topic) return { category, topic };
    }
  }
  return null;
}

export const HELP_CENTER_CATEGORIES: HelpCategory[] = [
  {
    id: 'demarrage',
    title: 'Démarrage & compte',
    description:
      'Prendre en main l’interface, votre profil et la navigation au quotidien dans Lyrra.',
    icon: Rocket,
    topics: [
      {
        id: 'demarrage-tableau-bord',
        title: "Tableau de bord formation",
        description:
          "Créer un cours, reprendre votre dernier travail et lire vos indicateurs dès la connexion.",
      },
      {
        id: 'demarrage-navigation',
        title: "Navigation & menu latéral",
        description:
          "Le menu de gauche, la recherche rapide, la langue, le thème et le menu de votre compte.",
      },
      {
        id: 'demarrage-profil',
        title: "Profil & préférences",
        description:
          "Vos informations, votre photo, votre mot de passe, vos notifications et la langue de l’interface.",
      },
      {
        id: 'demarrage-roles',
        title: "Rôles et droits d’accès",
        description:
          "Enseignant, professeur principal, direction, multi-site et élève : qui voit quoi.",
      },
      {
        id: 'demarrage-formation-simple',
        title: 'Création de formation simple',
        description:
          'Créer une formation en mode Simple : modules, chapitres et blocs sans le designer graphique EduFlow.',
      },
    ],
  },
  {
    id: 'eduflow',
    title: 'Parcours & designer EduFlow',
    description:
      'Concevoir des parcours pédagogiques avec blocs, connexions et logique de navigation.',
    icon: Puzzle,
    topics: [
      {
        id: 'eduflow-concept',
        title: "Vue d’ensemble du concepteur",
        description:
          "Barre d’outils, palette de blocs, canevas, panneau Propriétés et aperçu : le designer de parcours en un coup d’œil.",
      },
      {
        id: 'eduflow-blocs',
        title: "Types de blocs (contenu, quiz, médias…)",
        description:
          "Les familles de blocs de la palette, comment trouver le bon et comment le régler.",
      },
      {
        id: 'eduflow-branches',
        title: "Branches & conditions",
        description:
          "Orienter l’élève selon sa réponse au quiz, proposer des voies au choix et faire converger les branches.",
      },
      {
        id: 'eduflow-validation',
        title: "Validation du parcours",
        description:
          "Repérer et corriger ce qui empêche la publication : titres manquants, blocs sans suite, branches qui ne se rejoignent pas.",
      },
      {
        id: 'eduflow-objectifs',
        title: "Objectifs pédagogiques",
        description:
          "Écrire les objectifs du cours, les classer (savoir, savoir-faire, savoir-être, niveau de Bloom) et les relier aux blocs qui les travaillent.",
      },
      {
        id: 'eduflow-ia',
        title: "Assistant IA dans le designer",
        description:
          "Demander à l’IA d’écrire, de vérifier ou de modifier des blocs, et générer un QCM à partir de votre leçon, en gardant toujours la main.",
      },
    ],
  },
  {
    id: 'publication',
    title: 'Publication & diffusion',
    description:
      'Rendre un parcours disponible aux apprenants et partager les bons accès.',
    icon: Share2,
    topics: [
      {
        id: 'pub-statuts',
        title: 'Brouillon, publication et mises à jour',
        description:
          'Cycle de vie d’une formation : enregistrer, tester, publier et versionner les changements.',
      },
      {
        id: 'pub-liens',
        title: 'Liens d’accès & partage',
        description:
          'Générer ou copier les liens pour les apprenants, présentateurs ou prévisualisation.',
      },
      {
        id: 'pub-catalogue',
        title: 'Catalogue & visibilité',
        description:
          'Rattacher une formation au catalogue institutionnel ou à une offre lorsque c’est activé.',
      },
    ],
  },
  {
    id: 'apprenants',
    title: 'Apprenants, groupes & suivi',
    description:
      'Suivre les inscriptions, les cohortes et la progression des participants.',
    icon: Users,
    topics: [
      {
        id: 'app-inscriptions',
        title: 'Inscriptions & participants',
        description:
          'Consulter la liste des apprenants sur un parcours, leurs statuts et historiques d’accès.',
      },
      {
        id: 'app-groupes',
        title: 'Groupes & cohortes',
        description:
          'Utiliser les groupes pour segmenter les publics et faciliter les campagnes ou rapports.',
      },
      {
        id: 'app-progression',
        title: 'Progression & temps passé',
        description:
          'Interpréter la progression, les complétions et le temps d’engagement dans les blocs.',
      },
      {
        id: 'app-certificats',
        title: 'Certificats délivrés',
        description:
          'Vérifier les attestations générées et les conditions de réussite associées.',
      },
    ],
  },
  {
    id: 'medias',
    title: 'Médias & studio créatif',
    description:
      'Gérer bibliothèque, imports et outils audio / vidéo / image intégrés à Lyrra.',
    icon: Mic2,
    topics: [
      {
        id: 'medias-bibliotheque',
        title: 'Bibliothèque média',
        description:
          'Importer, organiser et réutiliser vidéos, sons, images et documents dans vos blocs.',
      },
      {
        id: 'medias-studio-audio',
        title: 'Studio audio & voix',
        description:
          'Enregistrer, mixer ou utiliser la synthèse voce pour enrichir vos contenus.',
      },
      {
        id: 'medias-video',
        title: 'Vidéos & intégrations',
        description:
          'Bonnes pratiques de format, poids et accessibilité pour les lecteurs intégrés.',
      },
      {
        id: 'medias-documents',
        title: 'Documents & PDF',
        description:
          'Joindre des ressources téléchargeables et lier la documentation au parcours.',
      },
    ],
  },
  {
    id: 'podcasts',
    title: 'Podcasts & livres audio',
    description:
      'Créer et diffuser des contenus audio longs format hors du parcours bloc par bloc.',
    icon: Headphones,
    topics: [
      {
        id: 'podcasts-creation',
        title: 'Création d’un podcast',
        description:
          'Structurer des épisodes, métadonnées et couvertures pour votre offre audio.',
      },
      {
        id: 'podcasts-diffusion',
        title: 'Diffusion & apprenants',
        description:
          'Paramètres d’écoute, partage et suivi d’usage côté institution.',
      },
    ],
  },
  {
    id: 'evaluation',
    title: 'Évaluation & pédagogie active',
    description:
      'Quiz, évaluations, sondages et retours pour mesurer les acquis.',
    icon: ClipboardCheck,
    topics: [
      {
        id: 'eval-quiz',
        title: 'Quiz & questions à choix',
        description:
          'Configurer barèmes, feedbacks et tentatives pour un QCM efficace.',
      },
      {
        id: 'eval-ouverte',
        title: 'Questions ouvertes & correction',
        description:
          'Organiser la collecte de réponses libres et la notation manuelle ou semi-automatisée.',
      },
      {
        id: 'eval-auto-eval',
        title: 'Auto-évaluation & remédiation',
        description:
          'Proposer des parcours adaptatifs en fonction des résultats.',
      },
    ],
  },
  {
    id: 'analytics',
    title: 'Analytiques & rapports',
    description:
      'Tableaux de bord, filtres et exports pour piloter l’impact des formations.',
    icon: LineChart,
    topics: [
      {
        id: 'analytics-vue',
        title: 'Vue d’ensemble analytique',
        description:
          'Personnaliser graphiques, périodes et filtre par formation pour analyser les données.',
      },
      {
        id: 'analytics-indicateurs',
        title: 'Indicateurs clés',
        description:
          'Taux de complétion, engagement, répartition par statut et temps sur les blocs.',
      },
      {
        id: 'analytics-exports',
        title: 'Exports & reporting RH',
        description:
          'Préparer les extractions pour le reporting interne ou réglementaire.',
      },
    ],
  },
  {
    id: 'institution',
    title: 'Établissement & équipe',
    description:
      'Paramétrer votre structure, les collaborateurs et l’organisation des contenus.',
    icon: Building2,
    topics: [
      {
        id: 'inst-collaborateurs',
        title: 'Utilisateurs & invitations',
        description:
          'Ajouter des formateurs, administrateurs ou RH et gérer les permissions.',
      },
      {
        id: 'inst-structure',
        title: 'Structure & périmètre',
        description:
          'Définir ce qui est partagé au niveau établissement versus projets personnels.',
      },
      {
        id: 'inst-trash',
        title: 'Corbeille & restauration',
        description:
          'Retrouver formations ou podcasts supprimés et les restaurer si besoin.',
      },
    ],
  },
  {
    id: 'marque',
    title: 'Marque & expérience utilisateur',
    description:
      'Aligner couleurs, libellés et parcours apprenant sur votre identité.',
    icon: Palette,
    topics: [
      {
        id: 'marque-charte',
        title: 'Charte & couleurs institutionnelles',
        description:
          'Comment la marque se reflète dans le player et les espaces apprenants.',
      },
      {
        id: 'marque-accessibilite',
        title: 'Accessibilité & lisibilité',
        description:
          'Contrastes, alternatives textuelles et navigation clavier dans les contenus.',
      },
      {
        id: 'marque-landing',
        title: 'Pages publiques & catalogue',
        description:
          'Présenter votre offre de formation côté visiteur lorsque le module est activé.',
      },
    ],
  },
  {
    id: 'qualite',
    title: 'Qualité, Qualiopi & conformité pédagogique',
    description:
      'Cadres qualité français : traçabilité, objectifs et preuves pour les audits.',
    icon: GraduationCap,
    topics: [
      {
        id: 'qualite-qualiopi',
        title: 'Pré-audit & indicateurs Qualiopi',
        description:
          'Utiliser les espaces dédiés pour préparer les critères et le dossier qualité.',
      },
      {
        id: 'qualite-tracabilite',
        title: 'Traçabilité pédagogique',
        description:
          'Relier objectifs, contenus et évaluations pour documenter le parcours certifiant.',
      },
    ],
  },
  {
    id: 'donnees',
    title: 'Données personnelles & sécurité',
    description:
      'RGPD, conservation, droits des personnes et bonnes pratiques de sécurité.',
    icon: Shield,
    topics: [
      {
        id: 'donnees-rgpd',
        title: 'RGPD & registre des traitements',
        description:
          'Finalités des données collectées dans Lyrra et responsabilité du responsable de formation.',
      },
      {
        id: 'donnees-conservation',
        title: 'Durée de conservation',
        description:
          'Principes de durée d’archivage des progressions et des preuves de formation.',
      },
      {
        id: 'donnees-mots-de-passe',
        title: 'Comptes & authentification',
        description:
          'Bonnes pratiques de mots de passe, sessions et délégation d’accès.',
      },
    ],
  },
  {
    id: 'support',
    title: 'Dépannage & support',
    description:
      'En cas de blocage technique ou fonctionnel, savoir où chercher et qui contacter.',
    icon: Wrench,
    topics: [
      {
        id: 'support-faq',
        title: 'Problèmes fréquents',
        description:
          'Lecteur qui ne charge pas, média manquant, erreur de publication : premiers réflexes.',
      },
      {
        id: 'support-navigateur',
        title: 'Navigateur & compatibilité',
        description:
          'Versions recommandées, cookies et extensions qui peuvent interférer.',
      },
      {
        id: 'support-contact',
        title: 'Contacter le support Lyrra',
        description:
          'Canal support, création de ticket et informations à fournir pour un diagnostic rapide.',
      },
    ],
  },
  {
    id: 'developpeurs',
    title: 'Développeurs & API',
    description:
      'Documentation HTTP de l’instance : OpenAPI (Swagger), préfixes REST et intégration MCP.',
    icon: Code2,
    topics: [
      {
        id: 'api-rest-openapi',
        title: 'API REST & OpenAPI (Swagger)',
        description:
          'Explorer toutes les routes HTTP, méthodes et réponses via Swagger UI et le fichier OpenAPI JSON.',
      },
      {
        id: 'mcp-lyrra',
        title: 'MCP & clés API',
        description:
          'URLs d’intégration, clés institution vs clés personnelles, variables d’environnement du serveur MCP.',
      },
    ],
  },
];
